'use client';

import { Stack } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { Toaster } from '@/components/ui/toaster';
import { persistor, store } from '../_store';
import InitializeSettings from './initialize';
import ThemeProvider from './theme';

import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import 'dayjs/locale/uz-latn';
// import 'dayjs/locale/kk'

import customParseFormat from 'dayjs/plugin/customParseFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { PersistGate } from 'redux-persist/integration/react';
import { useCustomLocale } from '../_hooks/useCustomLocale';
// import { GoogleOAuthProvider } from '@react-oauth/google'
// import { TelegramProvider } from './telegram';

dayjs.extend(updateLocale);
dayjs.extend(customParseFormat);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes - CRITICAL for memory management
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
});

function AppShell({ children }: { children: React.ReactNode }) {
  return (
    // <TelegramProvider>
    <>
      <InitializeSettings />
      <Stack gap={0} minH='100vh'>
        <Suspense fallback='Loading...'>
          <Toaster />
        </Suspense>
        {children}
      </Stack>
    </>
  );
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const locale = useCustomLocale();

  useEffect(() => {
    dayjs.locale(locale === 'uz' ? 'uz-latn' : locale);
  }, [locale]);

  const appShell = <AppShell>{children}</AppShell>;

  return (
    <ThemeProvider>
      <NuqsAdapter>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <PersistGate loading={appShell} persistor={persistor}>
              {appShell}
            </PersistGate>
          </Provider>
        </QueryClientProvider>
      </NuqsAdapter>
    </ThemeProvider>
  );
}
